<script setup lang="ts">
import HomeLayout from "~/layouts/HomeLayout.vue"
import { SUPPORTED_PAGES_ENUM } from "~/common/breadCrumbs"
/**
 *
 *  Props and emits section
 *
 *
 */

useSeoMeta({
    title: "Contacto",
    ogTitle: "Contacto - Dapamas",
})

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */

const defaultFormData = {
    name: "",
    email: "",
    message: "",
    privacy_policy_accepted: false,
    errors: {
        name: undefined,
        email: undefined,
        message: undefined,
    },
}

const formData = ref(defaultFormData)

const settingsStore = useDefaultSettingsStore()
const { settings } = storeToRefs(settingsStore)

const contacts = computed(() => {
    return settings.value?.contacts
})

const { data, error, loading, post } = useOFetchCustom("contact")

const notify = useNotification()
const i18n = useI18n()

const config = useRuntimeConfig()
const turnstileEnabled = computed(() =>
    Boolean(config.public.TURNSTILE_ENABLED)
)
const turnstileSiteKey = computed(
    () => (config.public.TURNSTILE_SITE_KEY as string) ?? ""
)

const turnstileToken = ref("")
const turnstileWidgetRef = ref<{ reset: () => void } | null>(null)

function resetTurnstile() {
    turnstileToken.value = ""
    turnstileWidgetRef.value?.reset()
}

// Submit is blocked until the privacy policy is accepted and (when enabled) the
// Turnstile challenge is solved.
const canSubmit = computed(
    () =>
        Boolean(formData.value.privacy_policy_accepted) &&
        (!turnstileEnabled.value || Boolean(turnstileToken.value))
)

/**
 *
 *  Life cicle events
 *
 *
 */

/**
 *
 *  Functions section
 *
 *
 */
function clearFormData() {
    formData.value.name = ""
    formData.value.email = ""
    formData.value.message = ""
    formData.value.privacy_policy_accepted = false
    formData.value.errors = {
        name: undefined,
        email: undefined,
        message: undefined,
    }
    resetTurnstile()
}

function submit() {
    if (!formData.value.privacy_policy_accepted) {
        notify("negative", i18n.t("messages.privacy_required"))
        return
    }

    if (turnstileEnabled.value && !turnstileToken.value) {
        notify("negative", i18n.t("messages.captcha_required"))
        return
    }

    const body = turnstileEnabled.value
        ? { ...formData.value, "cf-turnstile-response": turnstileToken.value }
        : formData.value

    post(body, {
        onSuccess: (res) => {
            notify("positive", res?.message)
            clearFormData()
        },
        onError: () => {
            // Token is single-use; force a fresh challenge for the next attempt.
            resetTurnstile()
        },
    })
}
</script>
<template>
    <HomeLayout
        :title="$t('home.contact_us')"
        :currentPage="SUPPORTED_PAGES_ENUM.ContactUs"
        withoutSteps
        withoutParentStyles
        withoutBanner
    >
        <section class="tw-pb-12 max-container">
            <!--Google maps-->
            <div
                class="tw-relative map-canvas tw-pb-[36%]"
                v-html="contacts?.map"
            ></div>

            <!--Contact form-->
            <div class="tw-flex tw-justify-center md:tw-mt-[-16%] tw-font-body">
                <q-card
                    class="tw-overflow-hidden tw-w-full sm:tw-w-[75%] xl:tw-w-[60%]"
                >
                    <div class="lg:tw-flex">
                        <div
                            class="bg-primary tw-p-5 tw-text-white tw-w-full md:tw-w-1/2"
                        >
                            <label class="tw-font-bold tw-text-lg">{{
                                $t("contacts.title")
                            }}</label>
                            <q-list class="tw-mt-4">
                                <div class="tw-flex tw-items-center tw-mb-4">
                                    <q-icon
                                        name="fa-solid fa-location-dot"
                                        color="white"
                                    />
                                    <span
                                        class="tw-text-gray-300 tw-text-base tw-pl-2 tw-w-[90%]"
                                    >
                                        Copenhague 20 interior 601 Colonia
                                        Juárez, alcaldía Cuauhtémoc CP 06600,
                                        Ciudad de México
                                    </span>
                                </div>

                                <div class="tw-flex tw-items-center tw-mb-4">
                                    <q-icon
                                        name="fa-solid fa-phone "
                                        color="white"
                                    />
                                    <span
                                        class="tw-text-gray-300 tw-text-base tw-pl-2 tw-w-[90%]"
                                    >
                                        (+52) 5620448115, (+52)5512023374
                                    </span>
                                </div>

                                <div class="tw-flex tw-items-center tw-mb-4">
                                    <q-icon
                                        name="fa-solid fa-envelope"
                                        color="white"
                                    />
                                    <span
                                        class="tw-text-gray-300 tw-text-base tw-pl-2 tw-w-[90%]"
                                    >
                                        contacto@dapamas.com
                                    </span>
                                </div>
                            </q-list>

                            <hr
                                class="tw-w-full tw-mx-auto tw-border-gray-300 tw-my-8"
                            />

                            <q-list class="tw-mt-4">
                                <div class="tw-flex tw-items-center tw-mb-4">
                                    <q-icon
                                        name="fa-solid fa-location-dot"
                                        color="white"
                                    />
                                    <span
                                        class="tw-text-gray-300 tw-text-base tw-pl-2 tw-w-[90%]"
                                    >
                                        Calle 30 # 1910 entre 19 y 21. Municipio
                                        Playa
                                    </span>
                                </div>

                                <div class="tw-flex tw-items-center tw-mb-4">
                                    <q-icon
                                        name="fa-solid fa-phone "
                                        color="white"
                                    />
                                    <span
                                        class="tw-text-gray-300 tw-text-base tw-pl-2 tw-w-[90%]"
                                    >
                                        (+53) 50995127
                                    </span>
                                </div>

                                <div class="tw-flex tw-items-center tw-mb-4">
                                    <q-icon
                                        name="fa-solid fa-envelope"
                                        color="white"
                                    />
                                    <span
                                        class="tw-text-gray-300 tw-text-base tw-pl-2 tw-w-[90%]"
                                    >
                                        contacto@dapamas.com
                                    </span>
                                </div>
                            </q-list>

                            <Brands class="tw-mt-3 tw-ml-2" />
                        </div>

                        <div
                            class="tw-p-5 tw-w-full tw-bg-customGray-100 md:tw-w-1/2"
                        >
                            <label class="tw-font-bold tw-text-lg">{{
                                $t("messages.leave_message")
                            }}</label>

                            <div
                                class="lg:tw-grid lg:tw-grid-cols-2 lg:tw-gap-5"
                            >
                                <TextInput
                                    v-model="formData.name"
                                    :label="$t('fields.name')"
                                    :errors="formData.errors.name"
                                />

                                <TextInput
                                    v-model="formData.email"
                                    :label="$t('fields.email')"
                                    :errors="formData.errors.email"
                                />

                                <TextInput
                                    class="tw-col-span-2 textarea-contact"
                                    v-model="formData.message"
                                    :label="$t('fields.message')"
                                    type="textarea"
                                    :errors="formData.errors.message"
                                />
                            </div>

                            <div class="tw-flex tw-items-center tw-mt-4">
                                <q-checkbox
                                    v-model="formData.privacy_policy_accepted"
                                    color="primary"
                                    size="sm"
                                    dense
                                />
                                <span
                                    class="tw-text-sm tw-ml-1 tw-text-customGray-700"
                                >
                                    {{
                                        $t("pre_footer.contact.accept_privacy")
                                    }}
                                    <NuxtLink
                                        to="/information/seg-priv-policy"
                                        class="tw-underline tw-font-semibold tw-text-primary"
                                        >{{
                                            $t(
                                                "settings.seg_and_privacy_policy"
                                            )
                                        }}</NuxtLink
                                    >
                                </span>
                            </div>

                            <TurnstileWidget
                                v-if="turnstileEnabled && turnstileSiteKey"
                                ref="turnstileWidgetRef"
                                v-model="turnstileToken"
                                size="normal"
                                :site-key="turnstileSiteKey"
                                class="tw-mt-4"
                                theme="light"
                            />

                            <div
                                class="tw-w-full tw-flex tw-justify-end tw-mt-12"
                            >
                                <PrimaryButton
                                    class="tw-text-lg tw-bg-primary btn-contact tw-mt-2"
                                    @click="submit"
                                    :loading="loading"
                                    :disable="!canSubmit"
                                >
                                    {{ $t("actions.send") }}
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                </q-card>
            </div>
        </section>
    </HomeLayout>
</template>
<style>
.map-canvas > iframe {
    position: absolute;
    width: 100%;
    height: 100%;
}
</style>
